import { MoneyText } from '@/components/MoneyText';
/**
 * Envelope proposal cards: their rows and actions (Create, Skip, Create all). Since TMMAPP-0141 §B
 * they live in the virtual account page's "Suggested envelopes" section, which owns the heading and
 * creates each one in that virtual account. Evidence + topPayees always shown; nothing created
 * without explicit Create / Create all.
 */
import { Box, Button, Chip, Grid, Stack, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import {
  envelopeEvidence,
  envelopePayload,
  type GenesisGroup,
  type GenesisProposal,
} from '@/api/genesis';
import { formatNumber } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';

export interface EnvelopeProposalsStepProps {
  proposals: GenesisProposal[];
  groups: GenesisGroup[];
  busy: boolean;
  onAccept: (id: number) => void;
  onDismiss: (id: number) => void;
  onAcceptAll: () => void;
}

export function EnvelopeProposalsStep({
  proposals,
  groups,
  busy,
  onAccept,
  onDismiss,
  onAcceptAll,
}: EnvelopeProposalsStepProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const currency = me.user.currency;
  const proposed = proposals.filter((p) => p.status === 'PROPOSED');

  function resolvePayeeLabel(raw: string): string {
    const asId = Number(raw);
    if (Number.isFinite(asId)) {
      const byId = groups.find((g) => g.payeeId === asId);
      if (byId?.displayName) return byId.displayName;
    }
    const byNorm = groups.find((g) => g.payeeNorm === raw || g.displayName === raw);
    if (byNorm?.displayName) return byNorm.displayName;
    return raw;
  }

  return (
    <Stack spacing={2}>
      {proposed.length > 0 && (
        <>
          <Button
            variant="outlined"
            disabled={busy}
            onClick={onAcceptAll}
            sx={{ alignSelf: 'flex-start' }}
          >
            {t('genesis.createAll', { count: proposed.length })}
          </Button>
          <Grid container spacing={2}>
            {proposed.map((p) => {
              const payload = envelopePayload(p);
              const evidence = envelopeEvidence(p);
              return (
                <Grid key={p.id} size={{ xs: 12, lg: 6 }}>
                  <Box
                    component="article"
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      height: '100%',
                    }}
                  >
                    <Stack spacing={1}>
                      <Typography variant="h3">
                        {payload.name ?? t('genesis.unnamedEnvelope')}
                      </Typography>
                      <Typography variant="body1">
                        <Trans
                          i18nKey="genesis.perMonth"
                          components={{
                            amount: (
                              <MoneyText minor={payload.targetMinor} currency={currency} whole />
                            ),
                          }}
                        />
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        <Trans
                          i18nKey="genesis.envelopeEvidence"
                          values={{
                            lines: evidence.lineCount ?? 0,
                            months:
                              evidence.monthsObserved != null
                                ? formatNumber(evidence.monthsObserved, locale)
                                : '—',
                          }}
                          components={{
                            median: (
                              <MoneyText minor={evidence.medianMonthMinor} currency={currency} />
                            ),
                            min: <MoneyText minor={evidence.minMonthMinor} currency={currency} />,
                            max: <MoneyText minor={evidence.maxMonthMinor} currency={currency} />,
                          }}
                        />
                      </Typography>
                      {evidence.topPayees.length > 0 && (
                        <Stack direction="row" sx={{ gap: 0.75, flexWrap: 'wrap' }}>
                          {evidence.topPayees.map((raw) => (
                            <Chip key={raw} size="small" label={resolvePayeeLabel(raw)} />
                          ))}
                        </Stack>
                      )}
                      <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap', pt: 0.5 }}>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={busy}
                          onClick={() => onAccept(p.id)}
                        >
                          {t('genesis.create')}
                        </Button>
                        <Button
                          size="small"
                          color="secondary"
                          disabled={busy}
                          onClick={() => onDismiss(p.id)}
                        >
                          {t('genesis.skip')}
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}
    </Stack>
  );
}
